import React from 'react';
import { useTenant } from '@/contexts/TenantContext';
import { generateAdDivId } from '@/config/ads.config';
import AdSlot from '@/components/ads/AdSlot';

// In development we render ad placeholders even when GAM is disabled for the tenant,
// so layout/positioning can be verified without live ad serving.
const showAdPlaceholders = import.meta.env.MODE === 'development';

/**
 * Top-of-page ad slot (placement key: TOP_PAGE).
 *
 * Render this immediately ABOVE the AppHeader on any screen that shows AppHeader.
 * Returns null unless the tenant has the TOP_PAGE placement enabled (and ads are
 * enabled, or we're running in development).
 */
export const TopPageAd: React.FC = () => {
  const { isAdsEnabled, adPlacements, isPlacementEnabled } = useTenant();

  // Resolve the concrete placement first: isPlacementEnabled is derived from the
  // raw placements list, but TenantContext drops placements with no adUnitPath, so
  // a placement can be "enabled" yet absent from adPlacements. Guard on the data
  // AdSlot/generateAdDivId actually dereference (adUnitPath + divIdPrefix).
  const placement = adPlacements?.TOP_PAGE;

  const show =
    !!placement &&
    !!placement.adUnitPath &&
    !!placement.divIdPrefix &&
    isPlacementEnabled('TOP_PAGE') &&
    (isAdsEnabled || showAdPlaceholders);

  if (!show || !adPlacements) return null;

  return (
    <div className="my-4">
      <AdSlot
        divId={generateAdDivId(adPlacements, 'TOP_PAGE', false)}
        adPlacement={placement}
        placement="top-page"
        className="rounded-lg overflow-hidden"
      />
    </div>
  );
};

/**
 * Below-header ad slot (placement key: BELOW_HEADER).
 *
 * Render this immediately BELOW the AppHeader on any screen that shows AppHeader.
 * Returns null unless the tenant has the BELOW_HEADER placement enabled (and ads
 * are enabled, or we're running in development).
 */
export const BelowHeaderAd: React.FC = () => {
  const { isAdsEnabled, adPlacements, isPlacementEnabled } = useTenant();

  // See TopPageAd: guard on the concrete placement so we never dereference an
  // enabled-but-missing BELOW_HEADER placement (adUnitPath null ⇒ filtered out).
  const placement = adPlacements?.BELOW_HEADER;

  const show =
    !!placement &&
    !!placement.adUnitPath &&
    !!placement.divIdPrefix &&
    isPlacementEnabled('BELOW_HEADER') &&
    (isAdsEnabled || showAdPlaceholders);

  if (!show || !adPlacements) return null;

  return (
    <div className="my-4">
      <AdSlot
        divId={generateAdDivId(adPlacements, 'BELOW_HEADER', false)}
        adPlacement={placement}
        enableRefresh={true}
        placement="below-header"
        className="rounded-lg overflow-hidden"
      />
    </div>
  );
};
